import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserPlus, Upload } from "lucide-react";
import api from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import CustomerModal from "./CustomerModal";
import CsvImportModal from "@/components/app/CsvImportModal";
import { btnOutline, btnPrimary, inr, pageSub, pageTitle } from "@/components/app/ui";

const initials = (n = "") => n.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
const tier = (c) => (c.orders_count >= 3 ? "loyal" : c.orders_count === 2 ? "repeat" : "new");

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);   // null = closed, {} = new, customer = edit
  const load = useCallback(() => api.get("/vendor/customers").then(({ data }) => setCustomers(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); api.get("/vendor/customer-categories").then(({ data }) => setCats(data)).catch(() => {}); }, [load]);

  const columns = useMemo(() => [
    { key: "name", header: "Customer", cell: (c) => (
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-[11px] font-bold flex items-center justify-center shrink-0">{initials(c.name)}</span>
        <span className="font-semibold text-neutral-900">{c.name}</span>
      </div>
    ) },
    { key: "email", header: "Email", hideBelow: "md", cell: (c) => <span className="text-slate-600">{c.email || "—"}</span> },
    { key: "mobile", header: "Mobile", hideBelow: "sm", cell: (c) => <span className="text-slate-600 font-mono text-[12px]">{c.mobile}</span> },
    { key: "category", header: "Category", hideBelow: "sm", accessor: (c) => c.category || "", cell: (c) => (c.category
      ? <span className="inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium bg-blue-50 text-blue-700 border-blue-200" data-testid="customer-category-badge">{c.category}</span>
      : <span className="text-slate-400">—</span>) },
    { key: "orders_count", header: "Orders", align: "center" },
    { key: "total_spent", header: "Total spent", align: "right", cell: (c) => <span className="font-semibold text-green-600">{inr(c.total_spent)}</span> },
  ], []);

  const filters = useMemo(() => [
    { key: "category", label: "All categories", accessor: (c) => c.category || "none", options: [...cats.map((n) => ({ value: n, label: n })), { value: "none", label: "No category" }] },
    { key: "tier", label: "All customers", accessor: tier, options: [{ value: "new", label: "New (1 order)" }, { value: "repeat", label: "Repeat (2 orders)" }, { value: "loyal", label: "Loyal (3+ orders)" }] },
  ], [cats]);

  return (
    <div className="space-y-4" data-testid="vendor-customers">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Customers</h1>
          <p className={pageSub}>{customers.length} customers. Click a customer to see their details.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImportOpen(true)} data-testid="import-customers-btn" className={btnOutline}><Upload size={15} /> Import CSV</button>
          <button onClick={() => setEditing({})} data-testid="add-customer-btn" className={btnPrimary}><UserPlus size={15} /> Add customer</button>
        </div>
      </div>
      <CsvImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} title="Import customers from CSV"
        subtitle="Add many customers at once, or update existing ones by mobile number." templateUrl="/vendor/customers/import-template" templateLabel="Download template" fileName="customers-import-template.csv"
        importUrl="/vendor/customers/import" columns="name, mobile, email, category, address, city, state, pincode, notes"
        hint="Required: name and a 10-digit mobile number (it identifies the customer). A mobile that already exists updates that customer (blank cells are left unchanged). Emails must be unique. Category is Retail, Wholesale, VIP or any of your own; a new name creates that category."
        summary={(r) => [{ count: r.created, label: "new" }, { count: r.updated, label: "to update" }, ...(r.failed ? [{ count: r.failed, label: "with errors", tone: "bad" }] : [])]} />
      <CustomerModal open={!!editing} customer={editing?.id ? editing : null} onClose={() => setEditing(null)} onSaved={load} />
      <DataTable testId="customers-table" exportName="customers" columns={columns} data={customers} loading={loading}
        searchKeys={["name", "email", "mobile"]} searchPlaceholder="Search name, email, mobile…" filters={filters}
        onRowClick={(c) => nav(`/vendor/customers/${c.id}`)} rowTestId={(c) => `customer-row-${c.id}`} defaultSort={{ key: "total_spent", dir: "desc" }} emptyIcon={Users} emptyTitle="No customers yet" emptyText="Customers appear after their first order, or add one yourself." />
    </div>
  );
}
